import { useEffect, useState, useCallback, useRef } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { motion } from 'framer-motion';
import { ArrowDown } from 'lucide-react';

// Metallic ping using Web Audio API
const playMetallicSound = () => {
  try {
    const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const filter = ctx.createBiquadFilter();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(4200, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(1800, ctx.currentTime + 0.08);
    filter.type = 'bandpass';
    filter.frequency.value = 3000;
    filter.Q.value = 15;
    gain.gain.setValueAtTime(0.06, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.15);
    osc.connect(filter).connect(gain).connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.15);
    setTimeout(() => ctx.close(), 200);
  } catch {}
};

interface WofEntry {
  name: string;
  team: string;
  image_url: string;
}

const staticEntries: WofEntry[] = [
  { name: 'Lili Chmelařová', team: 'Česká reprezentace žen', image_url: '/wof/lili-chmelarova.jpg' },
  { name: 'Tomáš Horák', team: 'Sønderjyske Vojens Vikings', image_url: '/wof/tomas-musil.jpg' },
  { name: 'Tereza Kulíšková', team: 'FBC Slavia Praha', image_url: '/wof/tereza-kuliskova.jpg' },
  { name: 'Aleš Pomajbík', team: 'HBC Hodonín', image_url: '/wof/ales-pomajbik.jpg' },
  { name: 'Andrej Smetana', team: 'HC Česká Lípa', image_url: '/wof/andrej-smetana.jpg' },
  { name: 'Petr Štaif', team: 'HC Energie K. Vary', image_url: '/wof/petr-staif.jpg' },
  { name: 'David Koutník', team: 'HC Milevsko', image_url: '/wof/david-koutnik.jpg' },
  { name: 'Ladislav Odehnal', team: 'HC Sharks Brno', image_url: '/wof/ladislav-odehnal.jpg' },
  { name: 'Petr Salaquarda', team: 'HC Slavoj Zbraslav', image_url: '/wof/petr-salaquarda.jpg' },
  { name: 'Vít Kohout', team: 'HC Verva Litvínov', image_url: '/wof/vit-kohout.jpg' },
  { name: 'Jiří Hána', team: 'HP Vimperk', image_url: '/wof/jiri-hana.jpg' },
  { name: 'Andrea Drdáková', team: 'OZ Na ľade – slov. hokejová škola', image_url: '/wof/andrea-drdakova.jpg' },
  { name: 'Matyáš Musil', team: 'SK Horácká Slavia Třebíč', image_url: '/wof/matyas-musil.jpg' },
  { name: 'Jonáš Caha', team: 'SK Horácká Slavia Třebíč', image_url: '/wof/jonas-caha.jpg' },
  { name: 'Jan Brož', team: 'SK Horácká Slávie Třebíč', image_url: '/wof/jan-broz.jpg' },
  { name: 'Sára Pánovová', team: 'Tatran Střešovice', image_url: '/wof/sara-panovova.jpg' },
];

const WallOfFame = () => {
  const { t } = useLanguage();
  const [entries, setEntries] = useState<WofEntry[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchEntries = async () => {
      const { data } = await supabase
        .from('wall_of_fame')
        .select('name, team, image_url')
        .eq('visible', true)
        .order('sort_order', { ascending: true });
      if (data && data.length > 0) {
        setEntries(
          data.map((d) => ({
            name: d.name,
            team: d.team || '',
            image_url: d.image_url || '',
          }))
        );
      }
      setLoaded(true);
    };
    fetchEntries();
  }, []);

  const items = entries.length > 0 ? entries : staticEntries;

  const scrollToGallery = () => {
    document.querySelector('#gallery')?.scrollIntoView({ behavior: 'smooth' });
  };

  if (!loaded) {
    return (
      <section className="py-20 md:py-32 px-4 bg-secondary/20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 space-y-4">
            <div className="h-12 md:h-16 w-64 md:w-96 mx-auto bg-muted/40 rounded-sm animate-pulse" />
            <div className="h-5 w-48 mx-auto bg-muted/30 rounded-sm animate-pulse" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[3/4] bg-gradient-to-br from-muted/40 to-muted/20 rounded-sm animate-pulse"
                style={{ animationDelay: `${i * 60}ms` }}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 md:py-32 px-4 bg-secondary/20 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2
            className="text-5xl md:text-7xl tracking-wider text-foreground"
            style={{ fontFamily: 'Bebas Neue' }}
          >
            {t.wallOfFame.title}
          </h2>
          <p className="mt-4 text-muted-foreground text-lg">
            {t.wallOfFame.subtitle}
          </p>
        </motion.div>

        {/* Marquee ticker */}
        <div className="relative mb-12 overflow-hidden py-4">
          <div className="flex animate-marquee whitespace-nowrap">
            {[...items, ...items].map((entry, i) => (
              <span
                key={i}
                className="mx-8 text-3xl md:text-4xl tracking-wider text-muted-foreground/20"
                style={{ fontFamily: 'Bebas Neue' }}
              >
                {entry.name}
              </span>
            ))}
          </div>
        </div>

        {/* Hockey cards grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
          {items.map((entry, i) => (
            <HockeyCard key={entry.name + i} entry={entry} index={i} />
          ))}
        </div>

        {/* Link to gallery */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <button
            onClick={scrollToGallery}
            className="inline-flex items-center gap-2 px-6 py-3 border border-primary/50 text-primary hover:bg-primary/10 transition-colors rounded-sm uppercase tracking-widest text-sm font-bold"
            style={{ fontFamily: 'Bebas Neue' }}
          >
            <ArrowDown size={18} />
            {t.wallOfFame.viewGallery}
          </button>
        </motion.div>
      </div>
    </section>
  );
};

const HockeyCard = ({ entry, index }: { entry: WofEntry; index: number }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const soundPlayed = useRef(false);

  const handleMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top) / rect.height - 0.5;
    el.style.transform = `perspective(600px) rotateY(${x * 20}deg) rotateX(${-y * 20}deg) translateY(-6px)`;
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (!soundPlayed.current) {
      playMetallicSound();
      soundPlayed.current = true;
    }
  }, []);

  const handleMouseLeave = useCallback(() => {
    const el = cardRef.current;
    if (el) {
      el.style.transform = '';
    }
    soundPlayed.current = false;
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.05 }}
      className="hockey-card group"
      onMouseEnter={handleMouseEnter}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      <div className="hockey-card-inner" ref={cardRef}>
        <div className="relative aspect-[3/4] overflow-hidden">
          {entry.image_url ? (
            <img
              src={entry.image_url}
              alt={entry.name}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full bg-muted/30 flex items-center justify-center">
              <span className="text-5xl text-muted-foreground/30" style={{ fontFamily: 'Bebas Neue' }}>
                {entry.name.charAt(0)}
              </span>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent pointer-events-none" />
          <div className="hockey-card-holo" />
          <span
            className="absolute top-2 right-2 text-3xl md:text-4xl text-amber-400/20 group-hover:text-amber-400/40 transition-colors duration-500 leading-none z-10 select-none"
            style={{ fontFamily: 'Bebas Neue' }}
          >
            #{String(index + 1).padStart(2, '0')}
          </span>
          <div className="absolute top-2 left-2 w-4 h-4 border-t-2 border-l-2 border-amber-400/40 group-hover:border-amber-400/80 transition-colors duration-500" />
          <div className="absolute bottom-0 left-2 w-4 h-4 border-b-2 border-l-2 border-amber-400/20 group-hover:border-amber-400/60 transition-colors duration-500" />
          <div className="absolute bottom-0 right-2 w-4 h-4 border-b-2 border-r-2 border-amber-400/20 group-hover:border-amber-400/60 transition-colors duration-500" />
          <div className="hockey-card-shine" />
        </div>

        <div
          className="absolute top-3 left-3 text-[8px] uppercase tracking-[0.3em] text-amber-400/15 group-hover:text-amber-400/30 transition-colors duration-500 z-10 select-none"
          style={{ fontFamily: 'Bebas Neue' }}
        >
          Tom Art Mask
        </div>

        <div className="relative px-3 py-3 bg-gradient-to-b from-black/90 to-card/95">
          <div className="w-8 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent mx-auto mb-2" />
          <span
            className="block text-lg md:text-xl tracking-wider text-amber-100 group-hover:text-amber-300 transition-colors duration-300 leading-tight"
            style={{ fontFamily: 'Bebas Neue' }}
          >
            {entry.name}
          </span>
          {entry.team && (
            <span className="block text-[11px] text-muted-foreground/70 mt-1 truncate uppercase tracking-widest">
              {entry.team}
            </span>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default WallOfFame;
